import { useState, useEffect } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Spinner,
} from "reactstrap";
import {
  getLeaveApplicationSummary,
  type LeaveSummaryMonth,
} from "../services/LeaveApplicationService";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function StatBlock({
  label,
  value,
  sub,
  badge,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex-fill" style={{ minWidth: "100px" }}>
      <p className="text-uppercase text-muted fw-medium mb-1" style={{ fontSize: "10px", letterSpacing: "0.6px" }}>
        {label}
      </p>
      <div className="fs-15 fw-semibold lh-1 mb-1">{value}</div>
      {sub && <div className="text-muted" style={{ fontSize: "11px" }}>{sub}</div>}
      {badge && <div className="mt-1">{badge}</div>}
    </div>
  );
}

function MonthStatGrid({ summary }: { summary: LeaveSummaryMonth }) {
  return (
    <div className="d-flex flex-wrap gap-3">
      <StatBlock
        label="Attendance"
        value={
          <>
            {summary.presentDays}
            <span className="text-muted fw-normal fs-13"> / {summary.threshold} days</span>
          </>
        }
        badge={
          summary.metThreshold
            ? <span className="badge bg-success-subtle text-success">Threshold met</span>
            : <span className="badge bg-danger-subtle text-danger">Threshold not met</span>
        }
      />

      <StatBlock
        label="Paid leaves"
        value={
          <>
            {summary.paidLeavesRemaining}
            <span className="text-muted fw-normal fs-13"> remaining</span>
          </>
        }
        sub={`${summary.paidLeavesTaken} of ${summary.paidLeaveQuota} used`}
      />

      {(summary.unpaidLeavesTaken > 0 || summary.nextLeaveIsLop) && (
        <StatBlock
          label="Unpaid / LOP"
          value={
            summary.unpaidLeavesTaken > 0
              ? <span className="text-warning">{summary.unpaidLeavesTaken}</span>
              : <span className="text-muted">—</span>
          }
          sub={summary.unpaidLeavesTaken > 0 ? "unpaid days" : undefined}
          badge={
            summary.nextLeaveIsLop
              ? <span className="badge bg-warning-subtle text-warning">Next leave is LOP</span>
              : undefined
          }
        />
      )}
    </div>
  );
}

function MultiMonthAccordion({ summaries }: { summaries: LeaveSummaryMonth[] }) {
  const firstKey = `${summaries[0].year}-${summaries[0].month}`;
  const [open, setOpen] = useState(firstKey);

  const toggle = (id: string) => setOpen((prev) => (prev === id ? "" : id));

  return (
    <Accordion open={open} toggle={toggle} className="custom-accordionwithicon">
      {summaries.map((s) => {
        const key = `${s.year}-${s.month}`;
        return (
          <AccordionItem key={key}>
            <AccordionHeader targetId={key}>
              <span className="fw-medium">
                {MONTH_NAMES[(s.month - 1) % 12]} {s.year}
              </span>
              <span className="d-flex gap-1 ms-2">
                {s.metThreshold
                  ? <span className="badge bg-success-subtle text-success">Met</span>
                  : <span className="badge bg-danger-subtle text-danger">Not met</span>}
                {s.nextLeaveIsLop && (
                  <span className="badge bg-warning-subtle text-warning">LOP</span>
                )}
              </span>
            </AccordionHeader>
            <AccordionBody accordionId={key}>
              <MonthStatGrid summary={s} />
            </AccordionBody>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export function LeaveSummaryPanel({
  fromDate,
  toDate,
  employeeId,
  refreshKey,
}: {
  fromDate?: string | null;
  toDate?: string | null;
  employeeId?: number | null;
  refreshKey?: number;
}) {
  const [summaries, setSummaries] = useState<LeaveSummaryMonth[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fromDate || !toDate) {
      setSummaries([]);
      return;
    }
    setLoading(true);
    setSummaries([]);
    getLeaveApplicationSummary({
      fromDate,
      toDate,
      employeeId: employeeId ?? undefined,
    })
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setSummaries(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fromDate, toDate, employeeId, refreshKey]);

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2 text-muted small mt-3">
        <Spinner size="sm" /> Loading summary…
      </div>
    );
  }

  if (!summaries.length) return null;

  if (summaries.length === 1) {
    return (
      <div className="mt-3">
        <MonthStatGrid summary={summaries[0]} />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <MultiMonthAccordion summaries={summaries} />
    </div>
  );
}
